import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, ShoppingCart, Quote, Sparkles } from "lucide-react";
import { SITE } from "@/config/site";
import BookCover from "@/components/BookCover";

function TopNav() {
  return (
    <header className="nav-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-slate-900">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="font-heading font-semibold">{SITE.brand}</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-primary"
          data-testid="book-back-home"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>
    </header>
  );
}

export default function ChasingAfterImpact() {
  const b = SITE.book;
  const cs = b.caseStudy;
  const navigate = useNavigate();

  return (
    <div data-testid="book-page">
      <TopNav />

      {/* HERO */}
      <section className="hero-grid">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1 fade-in-up">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
                The Book
              </span>
              <h1 className="font-heading mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                {b.title}
              </h1>
              <div className="gold-rule" />
              <p className="mt-4 text-lg sm:text-xl text-slate-700 leading-relaxed italic">
                {b.subtitle}
              </p>
              <p className="mt-3 text-sm text-slate-500">By {b.author}</p>

              <div className="mt-8 space-y-4 text-slate-700 leading-relaxed max-w-2xl">
                {b.heroBody.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-8">
                <a
                  href={b.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-hero-amazon"
                >
                  <Button
                    size="lg"
                    className="rounded-full px-8 py-6 text-base bg-primary hover:bg-teal-900"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" /> Buy on Amazon
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative">
                <div
                  className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-amber-100 via-primary/10 to-transparent blur-3xl"
                  aria-hidden="true"
                />
                <div className="relative">
                  <BookCover size="xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDY */}
      <section className="py-20 sm:py-24 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
              {cs.sectionTitle}
            </span>
            <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
              {cs.headline}
            </h2>
            <div className="gold-rule" />
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: text */}
            <div className="lg:col-span-7 space-y-4 text-slate-700 leading-relaxed">
              {cs.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}

              {/* Pull quote */}
              <blockquote
                className="mt-6 border-l-4 border-amber-400 bg-amber-50/60 px-6 py-5 rounded-r-lg"
                data-testid="book-pull-quote"
              >
                <Quote className="h-5 w-5 text-amber-500 mb-2" />
                <p className="font-heading text-lg sm:text-xl text-slate-900 italic leading-snug">
                  “{cs.pullQuote}”
                </p>
              </blockquote>

              <div className="mt-8">
                <a
                  href={b.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-case-amazon"
                >
                  <Button
                    size="lg"
                    className="rounded-full px-7 py-6 text-base bg-primary hover:bg-teal-900"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" /> Buy on Amazon
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: image */}
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mb-3">
                {cs.previewLabel}
              </div>
              <figure
                className="relative rounded-lg overflow-hidden shadow-xl ring-1 ring-slate-200 bg-white"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #fdfbf4 0%, #f5efdf 100%)",
                }}
                data-testid="book-case-image-frame"
              >
                {cs.previewImageUrl ? (
                  <img
                    src={cs.previewImageUrl}
                    alt="Preview from Chapter 1"
                    className="block w-full h-auto"
                  />
                ) : (
                  <div className="aspect-[3/4] w-full p-8 sm:p-10 flex flex-col text-slate-800">
                    <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-amber-700/80">
                      Chapter 1
                    </p>
                    <div className="mt-1 h-0.5 w-10 bg-amber-500/70" />
                    <h4 className="mt-4 font-heading text-xl sm:text-2xl font-semibold text-slate-900 leading-tight">
                      Why Impact is Important
                    </h4>
                    <div className="mt-6 space-y-3 text-sm leading-relaxed text-slate-600 flex-1 overflow-hidden">
                      <p>
                        <span className="font-heading text-3xl float-left mr-2 leading-none text-primary">
                          I
                        </span>
                        n every community, there are stories waiting to be told —
                        stories of people who show up early, stay late, and hold
                        space for others when no one is watching. When we chase
                        after impact instead of chasing after grants, the writing
                        changes. The language becomes clearer. The vision becomes
                        contagious…
                      </p>
                      <p className="opacity-70">
                        The pages that follow trace how a single after-school
                        room in Elizabeth, New Jersey, quietly became the
                        classroom for a career built on listening first.
                      </p>
                    </div>
                    <p className="mt-6 text-[10px] tracking-[0.25em] uppercase text-slate-400 text-right">
                      Page 10
                    </p>
                  </div>
                )}
              </figure>
              <figcaption className="mt-3 text-xs text-slate-500 italic text-center">
                {cs.previewCaption}
              </figcaption>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="paper-surface p-8 sm:p-12 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              {b.bottomCta.heading}
            </h2>
            <div className="gold-rule mx-auto" />
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Button
                size="lg"
                onClick={() => navigate("/build")}
                className="rounded-full px-7 py-6 text-base bg-primary hover:bg-teal-900"
                data-testid="book-bottom-start"
              >
                {b.bottomCta.startDraftLabel}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <a
                href={SITE.coaching.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-bottom-register"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 py-6 text-base border-slate-200 w-full sm:w-auto"
                >
                  {b.bottomCta.registerLabel}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} {SITE.brand}. For nonprofits and community builders.</div>
          <div>Helps draft proposal language. Does not guarantee funding.</div>
        </div>
      </footer>
    </div>
  );
}
